import { defineStore } from "pinia";
import { ref, computed, unref } from "vue";
import type { ZaptimeConfig } from "@zaptime/core";
import { languages } from "../languages";
import { watchDebounced } from "@vueuse/core";
import axios from "axios";
import route from "ziggy-js";
import { EventType } from "../types/EventType";
import useDays from "../useDays";

export const useEventTypeStore = defineStore("eventTypeStore", () => {
    const { days } = useDays();
    const eventType = ref<EventType>();

    const imageUrl = ref(
        "https://images.pexels.com/photos/718978/pexels-photo-718978.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    );
    const visibleMonthsInThePast = ref(0);
    const visibleMonthsInTheFuture = ref(2);
    const closestBookableDay = ref(1);

    const token = ref<string>("");

    const theme = ref<ZaptimeConfig["theme"]>();
    const introductionText = ref("Meet with Jane");

    const chooseDateLocaleText = ref("Choose date");
    const choosePreferredTime = ref("Choose preferred time");

    const noEventAvailableLocaleText = ref(
        "There is no event available for chosen month.",
    );

    const redirectAfterBookingUrl = ref<string>();

    const pickTimeLocaleText = ref("Pick a Time");
    const confirmBookingLocaleText = ref("Confirm booking");

    const hideTimePreferences = ref(false);

    const confirmationButtonLocaleText = ref("Confirm booking");
    const goBackButtonLocaleText = ref("Go back");

    const confirmationRescheduleButtonText = ref("Reschedule");
    const reschedulingEventText = ref("Rescheduling an enent");

    const compact = ref(false);

    const startDayOfTheWeek = ref(days.value[0]);
    const borderRadius = ref({
        radius: "0",
    });

    const selectedLanguage = ref(languages[0]);

    const config = computed((): ZaptimeConfig => {
        return {
            ...(import.meta.env.DEV && {
                apiBaseUrl: import.meta.env.VITE_API_URL,
            }),
            token: token.value,
            min: visibleMonthsInThePast.value,
            max: visibleMonthsInTheFuture.value,
            closestBookableDay: closestBookableDay.value,
            redirectAfterBookingUrl: redirectAfterBookingUrl.value,
            profileImage: imageUrl.value,
            externalBooking: false,
            compact: compact.value,
            locale: {
                preset: selectedLanguage.value.id,
                startDayOfWeek: startDayOfTheWeek.value.id,
                hideTimePreferences: hideTimePreferences.value,
                texts: {
                    chooseDate: chooseDateLocaleText.value,
                    noTimeSlotAvailable: noEventAvailableLocaleText.value,
                    introduction: introductionText.value,
                    pickTime: pickTimeLocaleText.value,
                    choosePreferredTime: choosePreferredTime.value,
                },

                confirmationForm: {
                    confirmBooking: confirmBookingLocaleText.value,
                    reschedulingEvent: reschedulingEventText.value,
                    buttons: {
                        confirmBooking: confirmationButtonLocaleText.value,
                        goBack: goBackButtonLocaleText.value,
                        reschedule: confirmationRescheduleButtonText.value,
                    },
                },
            },
            theme: theme.value,
        };
    });

    function save() {
        if (eventType.value === undefined) {
            throw new Error("Event Type needs to be defined");
        }

        type ConfigWithoutToken = Omit<ZaptimeConfig, "token">;

        const confCopy: ConfigWithoutToken = JSON.parse(
            JSON.stringify(config.value),
        );

        // @ts-expect-error
        delete confCopy.token;
        axios.patch(
            route("app.event-types.patch", {
                eventType: eventType.value.id,
            }),
            confCopy,
        );
    }

    watchDebounced(
        config,
        () => {
            save();
        },
        { debounce: 500 },
    );

    return {
        theme,
        config,
        introductionText,
        chooseDateLocaleText,
        choosePreferredTime,
        noEventAvailableLocaleText,
        pickTimeLocaleText,
        confirmBookingLocaleText,
        hideTimePreferences,
        confirmationButtonLocaleText,
        goBackButtonLocaleText,
        compact,
        startDayOfTheWeek,
        selectedLanguage,
        borderRadius,
        visibleMonthsInThePast,
        visibleMonthsInTheFuture,
        closestBookableDay,
        token,
        imageUrl,
        eventType,
        reschedulingEventText,
        confirmationRescheduleButtonText,
        redirectAfterBookingUrl,
    };
});
